import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Award, 
  Users, 
  Video, 
  Briefcase, 
  TrendingUp,
  ArrowRight,
  Calendar,
  Clock
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

export default function Home() {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const features = [
    { icon: <Users className="w-8 h-8" />, title: "15 Days Practical Digital Course", desc: "व्यावहारिक डिजिटल प्रशिक्षण" },
    { icon: <TrendingUp className="w-8 h-8" />, title: "Real Online Earning Strategies", desc: "असली ऑनलाइन कमाई की रणनीतियां" },
    { icon: <Video className="w-8 h-8" />, title: "Social Media Training", desc: "सोशल मीडिया प्रशिक्षण" },
    { icon: <Briefcase className="w-8 h-8" />, title: "Video Editing Training", desc: "वीडियो एडिटिंग प्रशिक्षण" },
    { icon: <Award className="w-8 h-8" />, title: "Personal Branding", desc: "व्यक्तिगत ब्रांडिंग" },
    { icon: <CheckCircle className="w-8 h-8" />, title: "Certificate Program", desc: "प्रमाणपत्र कार्यक्रम" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <WhatsAppButton />

      <section className="relative min-h-[600px] bg-gradient-to-br from-orange-50 via-white to-blue-50 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1648801098849-565ca6939c56?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-5"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h1 className="text-5xl md:text-7xl font-extrabold font-heading mb-6">
                <span className="gradient-text">MANISH KASYAP'S</span>
                <br />
                <span className="text-gray-900">Digital Research Platform</span>
              </h1>
              <p className="text-2xl md:text-3xl font-bold text-secondary mb-6 font-hindi">
                Learn • Earn • Promote • Grow
              </p>
              <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6 rounded-2xl mb-6 shadow-xl">
                <h2 className="text-2xl font-bold mb-2 font-hindi">
                  भारत का पहला एवं एकमात्र कोचिंग
                </h2>
                <p className="text-xl font-medium font-hindi">जो नौकर नहीं, मालिक बनाये</p>
              </div>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-3 rounded-xl shadow-md">
                  <Calendar className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="text-xs text-gray-600 font-hindi">पहला बैच</p>
                    <p className="font-bold text-gray-900 font-hindi">23 मार्च 2026</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-3 rounded-xl shadow-md">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-gray-600">Duration</p>
                    <p className="font-bold text-gray-900">15 Days</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/training" 
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2"
                  data-testid="enroll-now-button"
                >
                  Enroll Now
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/courses" 
                  className="bg-white text-orange-600 border-2 border-orange-500 px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl hover:bg-orange-50 transition-all duration-300"
                  data-testid="view-course-button"
                >
                  View Course
                </Link>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_digital-research-hub/artifacts/gnoer5gp_poster.png" 
                alt="Course Banner" 
                className="rounded-3xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold font-heading text-gray-900 mb-6">
              About <span className="gradient-text">Platform</span>
            </h2>
            <div className="max-w-4xl mx-auto text-lg text-gray-700 space-y-4 font-hindi">
              <p>
                Manish Kasyap Digital Research Platform एक आधुनिक डिजिटल प्रशिक्षण मंच है जिसका उद्देश्य युवाओं, महिलाओं और छोटे व्यवसायियों को डिजिटल शिक्षा देकर उन्हें आत्मनिर्भर बनाना है।
              </p>
              <p>
                आज के समय में सोशल मीडिया केवल मनोरंजन का साधन नहीं बल्कि रोजगार और पहचान बनाने का सबसे बड़ा माध्यम बन चुका है।
              </p>
              <p>
                इस प्लेटफॉर्म पर लोगों को सिखाया जाता है कि वे YouTube, Instagram, Facebook और अन्य डिजिटल प्लेटफॉर्म का सही उपयोग करके अपनी पहचान और आय कैसे बना सकते हैं।
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold font-heading text-gray-900 mb-4">
              Why <span className="gradient-text">Choose Us?</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-orange-500 hover:-translate-y-2 group"
                data-testid={`feature-card-${index}`}
              >
                <div className="text-orange-600 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 font-hindi">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold font-heading text-gray-900 mb-4">
              Meet Our <span className="gradient-text">Mentors</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-xl border-2 border-orange-100"
              data-testid="mentor-manish"
            >
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_digital-research-hub/artifacts/z76sumi5_manish%20Kasyap.png" 
                  alt="Manish Kasyap" 
                  className="w-32 h-32 rounded-full border-4 border-orange-500 shadow-lg object-cover"
                />
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Manish Kasyap</h3>
                  <p className="text-orange-600 font-semibold mb-3">Digital Guru</p>
                  <p className="text-gray-700 font-hindi leading-relaxed">
                    डिजिटल मीडिया के माध्यम से समाज से जुड़े मुद्दों को सामने लाने वाले प्रसिद्ध यूट्यूबर और सामाजिक कार्यकर्ता। उनका उद्देश्य युवाओं को डिजिटल दुनिया की सही जानकारी देकर उन्हें आत्मनिर्भर बनाना है।
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-3xl shadow-xl border-2 border-blue-100"
              data-testid="mentor-pankaj"
            >
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_digital-research-hub/artifacts/fia3kxn3_Pankaj%20Sir.jpg" 
                  alt="Pankaj Sir" 
                  className="w-32 h-32 rounded-full border-4 border-blue-500 shadow-lg object-cover"
                />
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Pankaj Sir</h3>
                  <p className="text-blue-600 font-semibold mb-3 font-hindi">35 वर्षों का शैक्षणिक अनुभव</p>
                  <p className="text-gray-700 font-hindi leading-relaxed">
                    पंकज सर एक अनुभवी और समर्पित शिक्षक हैं जिन्होंने हजारों छात्रों को शिक्षा और मार्गदर्शन दिया है। उनका उद्देश्य छात्रों के व्यक्तित्व विकास और करियर निर्माण में सहायता करना है।
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-orange-500 to-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-extrabold font-heading mb-6">
              Ready to Start Your Digital Journey?
            </h2>
            <p className="text-xl mb-8 font-hindi">
              आज ही नामांकन करें और अपने भविष्य को डिजिटल बनाएं!
            </p>
            <Link 
              to="/training" 
              className="inline-block bg-white text-orange-600 px-10 py-4 rounded-full font-bold text-lg shadow-2xl hover:scale-105 transition-all duration-300"
              data-testid="cta-enroll-button"
            >
              Enroll Now - ₹10,998 Only
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
